// PackingSheet.jsx — Backlog feature #8: printable packing sheet for ONE order.
// The sheet itself is components/PackingSheetView (shared with the bulk page
// /packing-bulk). Print via the browser (Ctrl+P); only the sheet prints
// (everything else is .no-print).

import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { db } from "../config/firebase";
import { doc, getDoc } from "firebase/firestore";
import { getAllProducts } from "../services/dataCache";
import PackingSheetView, { preparePackingSheet } from "../components/PackingSheetView";

export default function PackingSheet() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [productsBySku, setProductsBySku] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function load() {
      try {
        const orderSnap = await getDoc(doc(db, "orders", orderId));
        if (!orderSnap.exists()) {
          setError("Order not found.");
          setLoading(false);
          return;
        }
        const o = { id: orderSnap.id, ...orderSnap.data() };
        setOrder(o);

        // Resolve customer display name (company/contact), NOT the QB mapping
        // name. Orders placed on behalf of a customer RECORD name the record;
        // self-placed orders name the portal account. The users read is
        // guarded because reps may not read other users' docs (rules) — the
        // sheet still prints, just without the name.
        if (o.customerRecordId) {
          const rSnap = await getDoc(doc(db, "customers", o.customerRecordId));
          if (rSnap.exists()) {
            setCustomerName(rSnap.data().name || rSnap.data().qbCustomerName || "Unknown");
          }
        } else if (o.customerId) {
          try {
            const uSnap = await getDoc(doc(db, "users", o.customerId));
            if (uSnap.exists()) {
              const u = uSnap.data();
              setCustomerName(u.companyName || u.name || u.email || "Unknown");
            }
          } catch {
            // rep without permission to read this user — leave name unknown
          }
        }

        // Load products for name + storageMethod (session cache — 0 reads on revisit).
        const prods = await getAllProducts();
        setProductsBySku(Object.fromEntries(prods.map(p => [p.sku, p])));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [orderId]);

  if (loading) return <p>Loading packing sheet...</p>;
  if (error) return <div className="packing-page"><p className="error">{error}</p></div>;

  const { missingStorageSkus } = preparePackingSheet(order, productsBySku);
  if (missingStorageSkus.length > 0) {
    console.warn(
      `[packing sheet] Order ${order.orderNumber}: SKUs with no storage method ` +
      `(sorted last): ${missingStorageSkus.join(", ")}`
    );
  }

  return (
    <div className="packing-page">
      <div className="no-print packing-toolbar">
        <Link to="/admin/orders" className="clear-selection-btn">← Back to Orders</Link>
        <button className="print-btn" onClick={() => window.print()}>🖨 Print (Ctrl+P)</button>
      </div>

      {missingStorageSkus.length > 0 && (
        <div className="no-print storage-warning-banner">
          ⚠ These SKUs have no storage method set and were sorted last:{" "}
          <strong>{missingStorageSkus.join(", ")}</strong>. Set their storage
          method on the Products page so they group correctly.
        </div>
      )}

      <PackingSheetView order={order} customerName={customerName} productsBySku={productsBySku} />
    </div>
  );
}
